<template>
  <v-expansion-panels v-model="panel">
    <v-expansion-panel>
      <v-expansion-panel-header>
        {{ $t('ITEM.EDIT') }} {{ $t('TILES.ITEM_GROUP') }}
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <v-row>
          <v-col cols="12">
            <tiles-group-form
              v-model="group"
              :errors="errors.getAll()"
              @input="updateState"
              @valid="valid = $event"
              @update-prop="clearErrorByName($event)"
            ></tiles-group-form>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-btn
              dark
              class="ml-2 float-right"
              color="primary"
              @click="createTile"
            >
              <v-icon left>mdi-plus</v-icon>
              {{ $t('ITEM.ADD') }} {{ $t('TILES.ITEM') }}
            </v-btn>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <p class="text-subtitle-1">{{ $t('TILES.STRUCTURE_TITLE') }}:</p>
            <nested-draggable
              v-model="group.tiles"
              @end="updateState"
              @edit-tile="editTile"
              @remove-tile="removeTile"
            >
              <template #parent-title="{ item }">
                <span>{{ item.name }}</span>
                <v-spacer></v-spacer>
                <span class="grey--text mr-8">{{ item.type.name }}</span>
              </template>
              <template #title="{ item }">
                <span>{{ item.name }}</span>
                <v-spacer></v-spacer>
                <span class="grey--text mr-12">{{ item.type.name }}</span>
              </template>
            </nested-draggable>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-btn
              class="float-left"
              color="grey"
              large
              outlined
              depressed
              @click="$emit('cancel')"
            >
              {{ $t('BUTTON.CANCEL') }}
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              class="float-right"
              color="success"
              large
              depressed
              :disabled="!valid"
              @click="confirmUpdate"
            >
              {{ $t('BUTTON.SAVE') }}
            </v-btn>
          </v-col>
        </v-row>

        <v-dialog v-model="dialog" persistent max-width="800px">
          <template v-if="action">
            <component
              :is="`tile-${action}`"
              :id="currentTileId"
              :key="currentTileId"
              :tiles-group-id="id"
              @created="createdTile"
              @edited="editedTile"
              @cancel="cancelTile"
            ></component>
          </template>
        </v-dialog>
        <confirm-dialog ref="confirm"></confirm-dialog>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import { mapActions } from 'vuex';
import errors from '@/modules/_global/mixins/errors';

export default {
  name: 'TilesGroupEdit',

  components: {
    TileCreate: () => import('../Tile/TileCreate'),
    TileEdit: () => import('../Tile/TileEdit'),
    TilesGroupForm: () => import('./TilesGroupForm'),
    ConfirmDialog: () => import('@/modules/_global/_components/ConfirmDialog'),
    NestedDraggable: () =>
      import('@/modules/_global/_components/NestedDraggable')
  },

  mixins: [errors],

  props: {
    id: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      group: {
        tiles: []
      },
      unwatch: null,
      panel: 0,
      valid: true,
      action: null,
      dialog: false,
      currentTileId: null,
      removeTileDialog: false
    };
  },

  computed: {
    currentGroup() {
      return this.$store.getters['tile/GET_TILES_GROUP_BY_ID'](this.id);
    },
    isConfirmationSaving() {
      return this.group.tiles.length !== 6;
    }
  },

  watch: {
    currentGroup: {
      deep: true,
      immediate: true,
      handler: function(group) {
        this.group = {
          ...this.group,
          ...group
        };
      }
    }
  },

  destroyed() {
    this.unwatch();
  },

  mounted() {
    this.getTilesGroup({ tilesGroupId: this.id });
    this.unwatch = this.$store.watch(
      state => state.localization.lang,
      () => {
        const lang = this.$store.state.localization.lang;

        if (lang) {
          this.getTilesGroup({ tilesGroupId: this.id });
        }
      }
    );
  },

  methods: {
    ...mapActions('tile', [
      'getTilesGroup',
      'updateTilesGroup',
      'setTilesGroup',
      'destroyTile'
    ]),
    updateState() {
      this.setTilesGroup({ tilesGroup: this.group });
    },
    createTile() {
      this.action = 'create';
      this.dialog = true;
    },
    createdTile(tile) {
      this.group.tiles.push(tile);
      this.cancelTile();
      this.updateState();
    },
    editTile(tile) {
      this.currentTileId = tile.id;
      this.action = 'edit';
      this.dialog = true;
    },
    editedTile(tile) {
      this.cancelTile();
    },
    cancelTile() {
      this.dialog = false;
      this.action = null;
    },
    removeTile(tile) {
      this.$refs.confirm
        .open(`${this.$t('ITEM.DELETE')} ${this.$t('TILES.ITEM')}?`)
        .then(confirm => {
          if (confirm) {
            this.destroyTile({
              tileId: tile.id,
              tilesGroupId: this.id
            });
          }
        });
    },
    confirmUpdate() {
      if (this.isConfirmationSaving) {
        this.$refs.confirm
          .open(
            this.$t('TILES.UPDATE_CONFIRMATION_QUESTION_TITLE'),
            this.$t('TILES.UPDATE_CONFIRMATION_QUESTION_MESSAGE'),
            {
              width: 650,
              messageClass: 'text-center'
            }
          )
          .then(confirm => {
            if (confirm) {
              this.update();
            }
          });

        return;
      }

      this.update();
    },
    update() {
      this.updateTilesGroup({
        tilesGroupId: this.id,
        tilesGroup: this.group
      })
        .then(({ data }) => {
          this.$emit('edited', data);
        })
        .catch(({ response }) => {
          this.handleErrorsByResponse(response);
        });
    }
  }
};
</script>
